import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/common/Button';
import { Icon } from '../../../components/common/Icon';
import { useAppStore } from '../../../store/useAppStore';
import {
    formatFeedRelativeTime,
    getDashboardFeedPosts,
    getPostBadge,
    getPostPreview,
} from './production-feed-widget-utils';

export const ProductionFeedWidget: React.FC = () => {
    const navigate = useNavigate();
    const feedPosts = useAppStore(state => state.feedPosts || []);

    const visiblePosts = useMemo(
        () => getDashboardFeedPosts(feedPosts),
        [feedPosts]
    );

    return (
        <div
            className="bg-white dark:bg-surface-dark rounded-xl border border-gray-200 dark:border-border-dark p-6"
            data-testid="production-feed-widget"
        >
            <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <Icon name="dynamic_feed" className="text-primary" />
                    Production Feed
                </h3>
                {visiblePosts.length > 0 ? (
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {visiblePosts.length} recent
                    </span>
                ) : null}
            </div>

            {visiblePosts.length === 0 ? (
                <div className="text-center py-8">
                    <Icon name="forum" size="xl" className="text-gray-300 dark:text-gray-600 mb-2" />
                    <p className="text-gray-500 dark:text-gray-400 text-sm">No feed updates yet</p>
                    <p className="text-gray-400 dark:text-gray-500 text-xs mt-1">
                        Recent production updates will appear here.
                    </p>
                </div>
            ) : (
                <div className="space-y-3">
                    {visiblePosts.map(post => {
                        const badge = getPostBadge(post);
                        const mediaCount = post.media.length;
                        const linkCount = post.contentLinks.length;

                        return (
                            <article
                                key={post.id}
                                data-testid="production-feed-post"
                                className="rounded-lg border border-gray-100 dark:border-border-dark bg-gray-50/70 dark:bg-white/5 p-3"
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                                {post.authorName}
                                            </p>
                                            <span className="text-xs text-gray-400 dark:text-gray-500">
                                                {formatFeedRelativeTime(post.createdAt)}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-700 dark:text-gray-300 mt-1 line-clamp-2">
                                            {getPostPreview(post)}
                                        </p>
                                    </div>
                                    <span className={`inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium shrink-0 ${badge.className}`}>
                                        <Icon name={badge.icon} size="xs" />
                                        {badge.label}
                                    </span>
                                </div>

                                {mediaCount > 0 || linkCount > 0 ? (
                                    <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-gray-500 dark:text-gray-400">
                                        {mediaCount > 0 ? (
                                            <span className="inline-flex items-center gap-1">
                                                <Icon name="perm_media" size="xs" />
                                                {mediaCount} media
                                            </span>
                                        ) : null}
                                        {linkCount > 0 ? (
                                            <span className="inline-flex items-center gap-1">
                                                <Icon name="link" size="xs" />
                                                {linkCount} {linkCount === 1 ? 'link' : 'links'}
                                            </span>
                                        ) : null}
                                    </div>
                                ) : null}
                            </article>
                        );
                    })}
                </div>
            )}

            <Button
                variant="ghost"
                size="sm"
                onClick={() => { void navigate('/feed'); }}
                className="mt-4 w-full min-h-0 cursor-pointer gap-1 border-t border-gray-100 px-0 pt-3 pb-0 text-center text-xs text-primary hover:bg-transparent hover:text-primary/80 dark:border-border-dark"
            >
                View feed
                <Icon name="arrow_forward" size="xs" />
            </Button>
        </div>
    );
};
